/**
 * Created by Ning Jiang on 2021-05-04.
 */

import {ElGordosRevengeParsedData} from "./ElGordosRevengeParsedData";
import {IBetWayWinData, IElGordosRevengeGameData, ILastReelMultiFS} from "./IElGordosRevengeData";
import {ElGordosRevengeDataParser} from "./ElGordosRevengeDataParser";
import {IWinRatioConfig} from "../../../core/config/WinRatio";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {StackedSymbol} from "../../../core/reel/stackedsymbol/StackedSymbol";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {ElGordosRevengeReelController} from "../../reels/ElGordosRevengeReelController";

export class ElGordosRevengeParsedGameData extends ElGordosRevengeParsedData {

    public readonly xRip:boolean;
    public readonly singleWin:number;
    public readonly playedBetValue:number;
    public readonly wasFeatureBuy:boolean;
    public readonly brokeTheBank:boolean;
    public readonly lastReelMultiFS:ILastReelMultiFS;

    // Calculated values
    public betWins:IBetWayWinData[];
    public betWinsBeforeReveal:IBetWayWinData[];
    public winPositions:boolean[][];
    public readonly reelSplit:number[];
    public readonly showCylinder:boolean;
    public readonly lastReelEnabled:boolean;
    public readonly currentWinRatioConfig:IWinRatioConfig | null;

    // To add Spin Data
    public readonly nearWinReels:boolean[];
    public readonly nearWinBigWinReels:boolean[];

    // To add Nudge Data
    public readonly nudgeSteps:number[];

    constructor(rawData:IElGordosRevengeGameData, parser:ElGordosRevengeDataParser) {
        super(rawData, parser);

        this.xRip = rawData.xRip;
        this.singleWin = rawData.totalSpinWinnings;
        this.playedBetValue = rawData.playedBetValue;
        this.wasFeatureBuy = rawData.wasFeatureBuy;
        this.brokeTheBank = rawData.brokeTheBank;
        this.lastReelMultiFS = rawData.lastReelMultiFS;

        this.nudgeSteps = this.parseNudgeSteps();
        this.reelSplit = parser.parseReelSplit(this.S1Positions, this.mode);
        this.showCylinder = (this.reels[5][1] === "S2*0" && this.reels[5][2] === "S2*1");// || this.mode === GameMode.FREESPIN_SUPER;
        this.lastReelEnabled = this.parseLastReelEnabled(rawData);

        this.parseBetWayWins(rawData);
        this.currentWinRatioConfig = parser.parseWinRatio(this.singleWin / this.playedBetValue, rawData.mode);

        this.nearWinReels = parser.parseNearWinReels(this.S1Positions, this.wasFeatureBuy, this.mode);
        this.nearWinBigWinReels = this.getRandomBigWinNearWin();
    }

    private parseLastReelEnabled(rawData:IElGordosRevengeGameData):boolean {
        if(this.showCylinder) {
            return true;
        }

        return rawData.reels[5][1] == rawData.reels[5][2];
    }

    public parseBetWayWins(rawData:IElGordosRevengeGameData):void {
        this.betWins = [];
        this.betWinsBeforeReveal = [];
        this.winPositions = ArrayHelper.initArrayWithValues<boolean[]>(ElGordosRevengeReelController.SYMBOLS_NUM_IN_DATA.length, reelId => {
            return ArrayHelper.initArrayWithValues<boolean>(ElGordosRevengeReelController.SYMBOLS_NUM_IN_DATA[reelId][2], () => false)
        });
        rawData.betWayWins!.forEach(rawWin => {
            const betWin = {
                symbol : rawWin.symbol,
                amount : rawWin.amount,
                multiplier : rawWin.multiplier,
                multipliedAmount : rawWin.multipliedAmount,
                length : rawWin.length,
                numberOfCombinations : rawWin.numberOfCombinations,
                reelWinPositions : ArrayHelper.initArrayWithValues<boolean[]>(ElGordosRevengeReelController.SYMBOLS_NUM_IN_DATA.length, reelId => {
                    return ArrayHelper.initArrayWithValues<boolean>(ElGordosRevengeReelController.SYMBOLS_NUM_IN_DATA[reelId][2], () => false)
                })
            };
            const betWinBeforeReveal = {
                symbol : rawWin.symbol,
                amount : rawWin.amount,
                multiplier : rawWin.multiplier,
                multipliedAmount : rawWin.multipliedAmount,
                length : rawWin.length,
                numberOfCombinations : rawWin.numberOfCombinations,
                reelWinPositions : ArrayHelper.initArrayWithValues<boolean[]>(ElGordosRevengeReelController.SYMBOLS_NUM_IN_DATA.length, reelId => {
                    return ArrayHelper.initArrayWithValues<boolean>(ElGordosRevengeReelController.SYMBOLS_NUM_IN_DATA[reelId][2], () => false)
                })
            };
            for(let reelId = 0; reelId < rawWin.length; reelId++) {
                const reel = rawData.reels[reelId];
                const reelConfig = ElGordosRevengeReelController.SYMBOLS_NUM_IN_DATA[reelId];
                const symStartId = reelConfig[1];
                for(let symId = symStartId; symId < symStartId + reelConfig[2]; symId++) {
                    if(reel[symId].includes(rawWin.symbol) || reel[symId].includes("W") || reel[symId].includes("S2")) {
                        betWin.reelWinPositions[reelId][symId - symStartId] = true;
                        this.winPositions[reelId][symId - symStartId] = true;
                        betWinBeforeReveal.reelWinPositions[reelId][symId - symStartId] = true;
                    }
                    if(this.nudgeSteps[reelId] != 0) { // add full reel to win when nudge
                        betWin.reelWinPositions[reelId][symId - symStartId] = true;
                        this.winPositions[reelId][symId - symStartId] = true;
                    }
                    if(rawData.evaluatedArea[reelId][symId - symStartId].includes("W")) {
                        betWin.reelWinPositions[reelId][symId - symStartId] = true;
                        this.winPositions[reelId][symId - symStartId] = true;
                    }
                }
            }

            // if any revealed feature, update betWin, betWinBeforeReveal and winPositions.

            this.betWins.push(betWin);

            // Remove the betWinBeforeReveal if it only win with revealed symbol before reel 3.
            for(let rId = 0; rId < betWinBeforeReveal.length; rId++) {
                if(betWinBeforeReveal.reelWinPositions[rId].every((w) => !w)) {
                    if(rId < 3) {
                        return;
                    }
                    rId++;
                    while(rId < betWinBeforeReveal.length) {
                        betWinBeforeReveal.reelWinPositions[rId] = [false, false, false];
                        rId++;
                    }
                    break;
                }
            }
            this.betWinsBeforeReveal.push(betWinBeforeReveal);
        });

        // add scatter positions to allWinPositions.
        if(this.freespinTriggeredThisSpin) {
            this.S1Positions.forEach((symId, reelId) => {
                if(symId > -1) {
                    this.winPositions[reelId][symId] = true;
                }
            })
        }

        if(this.showCylinder) {
            this.winPositions[5][0] = true;
            this.winPositions[5][1] = true;
        }
    }

    private getRandomBigWinNearWin():boolean[] {
        const values = ArrayHelper.initArrayWithValues(ElGordosRevengeReelController.SYMBOLS_NUM_IN_DATA.length, () => false);
        values[4] = this.getRandomBigWinTease();
        if(values[4] && this.betWins.some(w => w.length > 4)) {
            values[5] = true;
        }

        return values;
    }

    private getRandomBigWinTease():boolean {
        if(this.wasFeatureBuy) {
            return false;
        }
        if(this.reelSplit.some(r => r > -1)) {
            return false;
        }
        if(this.showCylinder) {
            return false;
        }
        // if(this.totalWin === 0 || this.reels[4][1] === "S2") {
        //     return false;
        // }
        switch(this.mode) {
            // https://github.com/nolimitcity/tombstonerip/issues/99
            case GameMode.NORMAL:
                return this.currentWinRatioConfig!.ratio + Math.random() * 15 > 45;
            case GameMode.FREESPIN:
                return this.currentWinRatioConfig!.ratio + Math.random() * 20 > 60;
            case GameMode.FREESPIN_SUPER:
                return this.currentWinRatioConfig!.ratio + Math.random() * 300 > 600;
            default:
                return false;
        }
    }

    // The range is the start index to the end index of visible symbols in the reel data.
    // For a 3x5 slot, there are 3 symbols in reel data, then the range is [0, 3],
    // If you have a special feature that there are actually 5 symbols but you only show 3 symbols from the bottom of the 5 symbols, then you should return [2, 4].
    private parseNudgeSteps():number[] {
        return ArrayHelper.initArrayWithValues(ElGordosRevengeReelController.SYMBOLS_NUM_IN_DATA.length, reelId => {
            if(reelId < 1 || reelId > 4) { // TODO: change numbers for different reelsets
                return 0;
            }
            const reelConfig = ElGordosRevengeReelController.SYMBOLS_NUM_IN_DATA[reelId];
            const checkRange:[number, number] = [reelConfig[1], reelConfig[1] + reelConfig[2]];

            let symbolIndex:number = -1;
            let stackIndex:number = -1;

            for(let i:number = checkRange[0]; i < checkRange[1]; i++) {
                const stackedSymbolName = StackedSymbol.parseStackedSymbolName(this.reels[reelId][i], reelId);
                if(stackedSymbolName.symName === "WXN") {
                    symbolIndex = i;
                    stackIndex = stackedSymbolName!.index;
                    break;
                }
            }

            if(symbolIndex === -1) {
                return 0;
            }

            return checkRange[0] + stackIndex - symbolIndex;
        });
    }
}